// Full-page content preview shell: /brand/:brandId/campaigns/:campaignId/briefs/:briefId/preview
// (?context=<renderer context>&device=desktop|mobile). Toolbar: Back to post, the renderer's
// contexts as tabs, Desktop / Mobile; banners for unapproved content, missing media and a stale
// social card. Read-only: works for drafts too.
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { PreviewDevice } from "../../../shared/content-preview";
import { ContentPreview, PreviewBanners } from "../components/preview/ContentPreview";
import { resolveContext, resolveRenderer } from "../components/preview/registry";
import { useContentPreview } from "../hooks/useContentPreview";

function tabClass(active: boolean): string {
  return `min-h-9 rounded-md px-3 text-sm font-medium ${active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`;
}

export function ContentPreviewPage() {
  const { brandId, campaignId, briefId } = useParams<{ brandId: string; campaignId: string; briefId: string }>();
  const [params, setParams] = useSearchParams();
  const { model, isLoading, notFound, error } = useContentPreview(brandId, campaignId, briefId);
  const device: PreviewDevice = params.get("device") === "mobile" ? "mobile" : "desktop";
  const renderer = resolveRenderer(model?.surface);
  const context = resolveContext(renderer, params.get("context"));
  const backTo = brandId && campaignId && briefId ? `/brand/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}/briefs/${encodeURIComponent(briefId)}` : "/";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-200/60" data-testid="content-preview-page">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-2">
          <Link to={backTo} className="inline-flex min-h-9 items-center text-sm text-slate-700 hover:underline" data-testid="preview-back">← Back to post</Link>
          <span className="mx-2 hidden h-5 w-px bg-slate-200 sm:block" aria-hidden />
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{model ? `Preview · ${renderer.label}` : "Preview"}</p>
          {model && renderer.contexts.length > 1 && (
            <div role="group" aria-label="Where it appears" className="flex gap-1">
              {renderer.contexts.map((candidate) => (
                <button key={candidate.id} type="button" aria-pressed={candidate.id === context}
                  className={tabClass(candidate.id === context)} onClick={() => setParam("context", candidate.id)} data-testid={`preview-tab-${candidate.id}`}>
                  {candidate.label}
                </button>
              ))}
            </div>
          )}
          <div role="group" aria-label="Device width" className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
            {(["desktop", "mobile"] as const).map((option) => (
              <button key={option} type="button" aria-pressed={device === option} className={tabClass(device === option)}
                onClick={() => setParam("device", option)} data-testid={`preview-device-${option}`}>
                {option === "desktop" ? "Desktop" : "Mobile"}
              </button>
            ))}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-3 px-4 py-4">
        {isLoading ? (
          <p className="text-sm text-slate-600">Loading preview…</p>
        ) : notFound ? (
          <p role="alert" className="text-sm text-slate-700">This content is not in this campaign.</p>
        ) : !model ? (
          <p role="alert" className="text-sm text-slate-700">{error ? "The preview could not be loaded." : "Nothing to preview."}</p>
        ) : (
          <>
            <PreviewBanners model={model} />
            <ContentPreview model={model} context={context} device={device} />
          </>
        )}
      </main>
    </div>
  );
}
